import React, { useEffect, useRef, useState } from 'react';
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaClock } from 'react-icons/fa';
import { useTranslation, useLanguage } from '../../context/LanguageContext';
import { useLocalizedSettings } from '../../hooks/useLocalizedSettings';
import { IS_REACT_SNAP } from '../../utils/isReactSnap';

const ContactCard = ({ icon, label, children }) => (
    <div className="flex items-start space-x-4 p-5 sm:p-6 bg-gray-50 rounded-xl hover:shadow-md transition-shadow">
        <div className="flex-shrink-0 w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center text-gold text-xl">
            {icon}
        </div>
        <div>
            <h4 className="font-bold text-gray-800 mb-1">{label}</h4>
            {children}
        </div>
    </div>
);

/**
 * Home — Contacts: address, phone, email, hours + Google Map.
 *
 * The map mounts itself once scrolled near, never on click. It stays out of
 * the first render (and out of the pre-rendered HTML) because the embed costs
 * hundreds of kB of third-party JS, and its modern syntax crashes react-snap's
 * old Chromium, randomly leaving routes un-prerendered.
 */
export const HomeContactSection = () => {
    const [showMap, setShowMap] = useState(false);
    const mapSlotRef = useRef(null);
    const { t } = useTranslation();
    const { language } = useLanguage();
    const { settings, s, phoneHref } = useLocalizedSettings();

    useEffect(() => {
        if (IS_REACT_SNAP || showMap) return;

        const slot = mapSlotRef.current;
        if (!slot || typeof IntersectionObserver === 'undefined') {
            setShowMap(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => entry.isIntersecting && setShowMap(true),
            { rootMargin: '300px' }
        );
        observer.observe(slot);
        return () => observer.disconnect();
    }, [showMap]);

    const mapQuery = encodeURIComponent(
        settings.mapQuery || 'м. Івано-Франківськ, вулиця Красівського, 1'
    );
    const mapLang = language === 'en' ? 'en' : 'uk';

    return (
        <section id="contacts" className="py-14 sm:py-18 md:py-24 lg:py-28">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-10 sm:mb-14">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                        {t('contacts.title')}
                    </h2>
                    <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
                        {t('contacts.subtitle')}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
                    <div className="space-y-5">
                        <ContactCard icon={<FaMapMarkerAlt />} label={t('contacts.addressLabel')}>
                            <p className="text-gray-600 text-sm sm:text-base">{s('address')}</p>
                        </ContactCard>

                        <ContactCard icon={<FaPhoneAlt />} label={t('contacts.phoneLabel')}>
                            <a href={phoneHref} className="text-gold hover:underline text-sm sm:text-base font-medium">
                                {settings.phone}
                            </a>
                        </ContactCard>

                        <ContactCard icon={<FaEnvelope />} label={t('contacts.emailLabel')}>
                            <a href={`mailto:${settings.email}`} className="text-gold hover:underline text-sm sm:text-base font-medium">
                                {settings.email}
                            </a>
                        </ContactCard>

                        <ContactCard icon={<FaClock />} label={t('contacts.hoursLabel')}>
                            <p className="text-gray-600 text-sm sm:text-base">{s('hoursWeekdays')}</p>
                            <p className="text-gray-600 text-sm sm:text-base">{s('hoursSaturday')}</p>
                            <p className="text-gray-600 text-sm sm:text-base">{s('hoursSunday')}</p>
                        </ContactCard>
                    </div>

                    {/* Google Maps — auto-mounts on approach, see component docblock */}
                    <div
                        ref={mapSlotRef}
                        className="relative rounded-xl overflow-hidden shadow-lg h-[300px] sm:h-[400px] lg:h-full min-h-[350px] bg-gray-100"
                    >
                        {showMap ? (
                            <iframe
                                title={t('contacts.mapTitle')}
                                src={`https://maps.google.com/maps?q=${mapQuery}&hl=${mapLang}&t=&z=16&ie=UTF8&iwloc=&output=embed`}
                                className="w-full h-full border-0"
                                allowFullScreen=""
                                loading="lazy"
                                referrerPolicy="no-referrer-when-downgrade"
                            />
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                                <span className="w-14 h-14 rounded-full bg-gold/10 text-gold text-2xl flex items-center justify-center">
                                    <FaMapMarkerAlt />
                                </span>
                                <p className="text-gray-700 font-semibold">{s('address')}</p>
                                <a
                                    href={`https://www.google.com/maps/search/?api=1&query=${mapQuery}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-gray-500 underline hover:text-gold transition-colors focus:outline-none focus:ring-2 focus:ring-gold/40 rounded"
                                >
                                    {t('contacts.openInMaps')}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
};

